import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react"
import { Platform } from "react-native"

// v2 : pivot « composition par sessions » — le state ne porte plus
// variantKey/startDate/subjects mais sessions[] + sessionSubjects{} ; la clé
// versionnée écarte un blob v1 (formule × date) qui polluerait la reprise.
const STORAGE_KEY = "stage_funnel_state_v2"

export const STAGE_FUNNEL_INITIAL = {
    funnelStateId: null,
    // Pré-rempli depuis la LP (submissionGUID HubSpot ou query params).
    studentFirstname: "",
    parentEmail: "",
    submissionGUID: null,
    // Routage catalogue : lead_intent brut (préfixé stage_, résolu côté backend)
    // + slug du template résolu (renvoyé par /init ou porté par l'URL d'entrée).
    leadIntent: null,
    templateSlug: null,
    // Nom du stage (template) — servi par la config API, jamais codé en dur.
    templateName: null,
    // Étape classe — année en cours + rentrée (dérivée, corrigeable).
    studentClass: null,
    studentClassRentree: null,
    // Élève abonné reconnu par le lien : le serveur connaît sa classe (celle
    // du profil) et ses spécificités, le parcours commence au choix des
    // semaines et ne revient pas sur ces deux étapes.
    profileKnown: false,
    // Étape diagnostic — objectif / niveau ressenti / besoins spécifiques élève
    // (chips dys/TDAH… — PAS de champ texte libre dans ce MVP).
    objective: null,
    niveauActuel: null,
    besoinsSpeciaux: [],
    // Issue de secours du compositeur : la famille garde le programme proposé
    // et décidera des chapitres plus tard avec l'élève.
    programDecidedLater: false,
    // Étape composition « par sessions » — sessions cochées (dates ISO de
    // début, triées, non contiguës OK), matières PAR session cochée
    // ({ "2026-08-03": [subjectIds…] }, cap servi par le backend) et horaire
    // hebdo (slot du template — auto-sélectionné et masqué s'il n'y en a
    // qu'un). Tout vient du catalogue API — le prix suit le nb de sessions.
    sessions: [],
    sessionSubjects: {},
    // Noms des matières choisies (id → nom), stampés au moment de la sélection
    // (le configurateur a déjà chargé le catalogue matières). Purement
    // d'affichage : permet au récap du checkout d'afficher les matières
    // INSTANTANÉMENT dans le flux avant, sans attendre un second fetch (sinon
    // la matière « pop » sous la session). Vide sur reprise à froid (le back ne
    // renvoie que les ids) → le checkout retombe sur un fetch de secours.
    subjectNames: {},
    slotKey: null,
    paid: false,
    // Étiquette `source` du lien d'entrée (bannière, relance…) : envoyée à
    // /start et portée par chaque événement `stage_funnel_*`.
    entrySource: null,
    // Jeton `user` sur lequel la reprise a déjà statué — la garde ne la
    // redemande pas pour lui (voir useStageFunnelGuard).
    resumedToken: null,
    // … et le stage du lien pour lequel elle a statué.
    resumedTemplate: null,
}

const loadFromStorage = () => {
    if (Platform.OS !== "web") return null
    try {
        const raw = window?.sessionStorage?.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (_e) {
        return null
    }
}

const saveToStorage = (state) => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (_e) {
        /* noop */
    }
}

const clearStorage = () => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.removeItem(STORAGE_KEY)
    } catch (_e) {
        /* noop */
    }
}

const StageFunnelContext = createContext(null)

/**
 * Provider UNIQUE du funnel Stage — monté une seule fois au-dessus du
 * navigateur racine (RootNavigator), jamais par écran. Même architecture que le
 * SummerFunnelProvider (dont ce funnel est le clone) : l'état vit en mémoire et
 * est partagé nativement entre tous les écrans stage (web ET natif) ; le
 * sessionStorage n'est qu'un filet de survie au refresh (web) — relu au boot,
 * jamais comme canal cross-écran.
 */
export const StageFunnelProvider = ({ children }) => {
    const [state, setState] = useState(() => ({
        ...STAGE_FUNNEL_INITIAL,
        ...(loadFromStorage() || {}),
    }))

    const update = useCallback((patch) => {
        setState((prev) => {
            const next = { ...prev, ...patch }
            saveToStorage(next)
            return next
        })
    }, [])

    const reset = useCallback(() => {
        clearStorage()
        setState(STAGE_FUNNEL_INITIAL)
    }, [])

    const value = useMemo(
        () => ({ state, update, reset }),
        [state, update, reset]
    )

    return (
        <StageFunnelContext.Provider value={value}>
            {children}
        </StageFunnelContext.Provider>
    )
}

export const useStageFunnel = () => {
    const ctx = useContext(StageFunnelContext)
    if (ctx) return ctx
    return {
        state: STAGE_FUNNEL_INITIAL,
        update: () => {},
        reset: () => {},
    }
}
